import { useState, useEffect, useCallback } from 'react';
import { PresupuestoService } from '../services/presupuesto.service';
import { useAuth } from '../../auth/hooks/useAuth';
import { useEgresosOptimistic } from '../../egresos/hooks/useEgresosOptimistic';
import type { Presupuesto, EstadoPresupuesto, AlertaPresupuesto, ConfiguracionAlertas } from '../../../shared/types/presupuestos';
import { LoggerService } from '../../../shared/services/logger.service';

export const usePresupuestos = () => {
  const { user } = useAuth();
  const { egresos } = useEgresosOptimistic();
  
  const [presupuestos, setPresupuestos] = useState<Presupuesto[]>([]);
  const [estadosPresupuesto, setEstadosPresupuesto] = useState<EstadoPresupuesto[]>([]);
  const [alertas, setAlertas] = useState<AlertaPresupuesto[]>([]);
  const [configuracionAlertas, setConfiguracionAlertas] = useState<ConfiguracionAlertas>({
    emailEnabled: true,
    pushEnabled: true,
    alertaAdvertencia: 80,
    alertaLimite: 100
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar datos iniciales
  const cargarDatos = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      setError(null);

      // Cargar presupuestos
      const presupuestosUsuario = PresupuestoService.obtenerPresupuestos(user.uid);
      setPresupuestos(presupuestosUsuario);

      // Calcular estados de presupuestos
      const estados = PresupuestoService.calcularEstadoPresupuestos(user.uid, egresos);
      setEstadosPresupuesto(estados);

      // Generar alertas si es necesario
      PresupuestoService.generarAlertas(user.uid, estados);
      const todasLasAlertas = PresupuestoService.obtenerAlertas(user.uid);
      setAlertas(todasLasAlertas);

      // Cargar configuración
      const config = PresupuestoService.obtenerConfiguracionAlertas(user.uid);
      setConfiguracionAlertas(config);

      LoggerService.info('Datos de presupuestos cargados', 'usePresupuestos', {
        presupuestos: presupuestosUsuario.length,
        alertas: todasLasAlertas.length
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error cargando presupuestos';
      setError(errorMessage);
      LoggerService.error('Error cargando presupuestos', 'usePresupuestos', err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, egresos]);

  // Crear presupuesto
  const crearPresupuesto = useCallback(async (datos: Omit<Presupuesto, 'id' | 'userId' | 'fechaCreacion' | 'fechaActualizacion'>) => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    try {
      const nuevoPresupuesto = PresupuestoService.crearPresupuesto(user.uid, datos);
      await cargarDatos(); // Recargar todos los datos
      return nuevoPresupuesto;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error creando presupuesto';
      LoggerService.error('Error creando presupuesto', 'usePresupuestos', err);
      throw new Error(errorMessage);
    }
  }, [user?.uid, cargarDatos]);

  // Actualizar presupuesto
  const actualizarPresupuesto = useCallback(async (presupuestoId: string, datos: Partial<Presupuesto>) => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    try {
      const exito = PresupuestoService.actualizarPresupuesto(user.uid, presupuestoId, datos);
      if (exito) {
        await cargarDatos(); // Recargar todos los datos
      }
      return exito;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error actualizando presupuesto';
      LoggerService.error('Error actualizando presupuesto', 'usePresupuestos', err);
      throw new Error(errorMessage);
    }
  }, [user?.uid, cargarDatos]);

  // Eliminar presupuesto
  const eliminarPresupuesto = useCallback(async (presupuestoId: string) => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    try {
      const exito = PresupuestoService.eliminarPresupuesto(user.uid, presupuestoId);
      if (exito) {
        await cargarDatos(); // Recargar todos los datos
      }
      return exito;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error eliminando presupuesto';
      LoggerService.error('Error eliminando presupuesto', 'usePresupuestos', err);
      throw new Error(errorMessage);
    }
  }, [user?.uid, cargarDatos]);

  // Marcar alerta como leída
  const marcarAlertaLeida = useCallback(async (alertaId: string) => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    try {
      const exito = PresupuestoService.marcarAlertaLeida(user.uid, alertaId);
      if (exito) {
        // Actualizar alertas localmente
        setAlertas(prev => prev.map(alerta => 
          alerta.id === alertaId ? { ...alerta, leida: true } : alerta
        ));
      }
      return exito;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error marcando alerta como leída';
      LoggerService.error('Error marcando alerta como leída', 'usePresupuestos', err);
      throw new Error(errorMessage);
    }
  }, [user?.uid]);

  // Actualizar configuración de alertas
  const actualizarConfiguracionAlertas = useCallback(async (config: ConfiguracionAlertas) => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    try {
      const exito = PresupuestoService.actualizarConfiguracionAlertas(user.uid, config);
      if (exito) {
        setConfiguracionAlertas(config);
      }
      return exito;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error actualizando configuración';
      LoggerService.error('Error actualizando configuración de alertas', 'usePresupuestos', err);
      throw new Error(errorMessage);
    }
  }, [user?.uid]);

  // Obtener presupuesto por categoría
  const obtenerPresupuestoPorCategoria = useCallback((categoria: string): Presupuesto | undefined => {
    return presupuestos.find(p => p.categoria === categoria && p.activo);
  }, [presupuestos]);

  // Obtener estado de presupuesto por categoría
  const obtenerEstadoPorCategoria = useCallback((categoria: string): EstadoPresupuesto | undefined => {
    return estadosPresupuesto.find(e => e.presupuesto.categoria === categoria);
  }, [estadosPresupuesto]);

  // Obtener alertas no leídas
  const alertasNoLeidas = useCallback(() => {
    return alertas.filter(alerta => !alerta.leida);
  }, [alertas]);

  // Obtener resumen general
  const resumenPresupuestos = useCallback(() => {
    const total = presupuestos.filter(p => p.activo).length;
    const superados = estadosPresupuesto.filter(e => e.estado === 'superado').length;
    const advertencias = estadosPresupuesto.filter(e => e.estado === 'advertencia').length;
    const normales = estadosPresupuesto.filter(e => e.estado === 'normal').length;
    
    return {
      total,
      normales,
      advertencias,
      superados,
      alertasPendientes: alertasNoLeidas().length
    };
  }, [presupuestos, estadosPresupuesto, alertasNoLeidas]);

  // Cargar datos cuando cambie el usuario o los egresos
  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  return {
    // Estados
    presupuestos,
    estadosPresupuesto,
    alertas,
    configuracionAlertas,
    loading,
    error,
    
    // Acciones
    crearPresupuesto,
    actualizarPresupuesto,
    eliminarPresupuesto,
    marcarAlertaLeida,
    actualizarConfiguracionAlertas,
    cargarDatos,
    
    // Helpers
    obtenerPresupuestoPorCategoria,
    obtenerEstadoPorCategoria,
    alertasNoLeidas,
    resumenPresupuestos
  };
};